<template>
  <UITooltip
    v-if="isExpiring"
    class="ChatHistoryRow__tooltip"
    placement="top"
    :offset="[0, 8]"
  >
    <template #trigger>
      <ChatHistoryLink
        :chat="chat"
        :showContextMenu="showContextMenu"
        :isExpiring="true"
        @onChatClick="$emit('onChatClick')"
      />
    </template>

    <template #content>
      This chat will be deleted in
      {{ formatDaysUntilDeletion(daysUntilDeletion) }} due to inactivity. Open
      or continue the conversation to keep it active.
    </template>
  </UITooltip>

  <ChatHistoryLink
    v-else
    :chat="chat"
    :showContextMenu="showContextMenu"
    @onChatClick="$emit('onChatClick')"
  />
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { UITooltip } from '@foundever/ui-lib'

import type { IChatListItem } from '@/apiClient/types/chat'
import { useRetentionClock } from '@/composables/useRetentionClock'
import {
  formatDaysUntilDeletion,
  getChatDeletionInfo,
} from '@/store/chat/utils/chatExpiry'

import ChatHistoryLink from './ChatHistoryLink.vue'

defineEmits(['onChatClick'])
const props = withDefaults(
  defineProps<{
    chat: IChatListItem
    /**
     * If true, the context menu will be shown when the user hovers on a chat
     */
    showContextMenu?: boolean
  }>(),
  {
    showContextMenu: true,
  },
)

// Pinning does not exempt a chat from retention, so a pinned row warns about its
// deletion exactly like any other (see the sidebar's "Expiring soon" ordering).
// Read through the shared clock so a row crossing into the window starts warning
// without waiting for the list to refetch.
const retentionNow = useRetentionClock()
const deletionInfo = computed(() =>
  getChatDeletionInfo(props.chat, retentionNow.value),
)
const isExpiring = computed(() => deletionInfo.value?.isExpiringSoon ?? false)
const daysUntilDeletion = computed(
  () => deletionInfo.value?.daysUntilDeletion ?? 0,
)
</script>

<style lang="scss">
// The ui-lib UITooltip wraps its trigger in an inline-flex anchor, which shrink-to-fits
// to the row's max-content width: a long title never ellipsizes, the expiring badge
// trails the text instead of sitting at the row's end, and the sidebar's scroll area
// grows a horizontal scrollbar. Forcing it block restores the full-width layout.
//
// The class rides `$attrs` onto that anchor. A `:deep()` rule cannot reach it — the
// tooltip is this component's root, so the anchor carries the scope id itself rather
// than sitting under an element that does, and `[data-v-x] .UITooltip__trigger` never
// matches.
.ChatHistoryRow__tooltip {
  display: block;
  width: 100%;
}
</style>
